<template>
  <div
    class="grid w-full gap-12 p-16 md:p-24 lg:gap-20"
    :class="{
      'text-static-default-hi-disabled': isOutOfStock,
      [$style.productGrid]: true,
      [$style.productGridOutOfStock]: isOutOfStock,
    }"
    :data-qa="'cart-product-' + item.listingId"
    data-test="cart-product"
  >
    <ProductIllustration
      class="size-60 md:size-[110px]"
      :class="{
        'opacity-50 grayscale-[50%]': isOutOfStock,
      }"
      :image="item.image"
      :link="item.link"
      :style="{ gridArea: 'illustration' }"
      :title="item.title"
    />

    <Description
      :categoryId="item.marketplaceCategoryId"
      :gradeId="item.gradeId"
      :gradeName="item.gradeName"
      :hasNewBattery="item.gradeExtended?.hasNewBattery ?? false"
      :is-out-of-stock
      :link="item.link"
      :style="{ gridArea: 'title' }"
      :title="item.title"
    />

    <ShippingPromise
      v-if="!isOutOfStock && item.options?.[0]?.choices"
      :choices="item.options[0].choices"
      class="body-2"
      :style="{ gridArea: 'delivery' }"
    />

    <PriceBlock :is-out-of-stock :item :style="{ gridArea: 'price' }" />

    <div
      v-if="!isCartLocked"
      class="flex items-center gap-8"
      :class="{
        'justify-end': isOutOfStock,
        'justify-between md:justify-end': !isOutOfStock,
      }"
      :style="{ gridArea: 'manage' }"
    >
      <div
        v-if="!isOutOfStock"
        class="flex items-center gap-8 md:flex-row-reverse"
      >
        <ArticleQuantitySelector
          :id="`quantity-${item.listingId}`"
          :disabled="item.mobilePlan !== null"
          :limit="10"
          :listing-title="item.title"
          :max="item.quantityMax"
          :model-value="item.quantity"
          @update:model-value="handleQuantitySelectorChange"
        />

        <Stock :item />
      </div>

      <RevButton
        class="ml-12"
        data-qa="delete-item-button"
        :loading="isRemoving"
        size="small"
        variant="secondaryDestructive"
        @click="handleDeleteButtonClick"
      >
        {{ i18n(translations.productRemove) }}
      </RevButton>
    </div>
  </div>

  <div
    v-if="
      isOutOfStock && (!replacements || !replacements.length) && !isCartLocked
    "
    class="p-16 pt-0 md:p-24 md:pt-0"
  >
    <OutOfStock
      :is-loading="replacementsStatus === 'pending'"
      :item
      @remove="handleDeleteButtonClick"
    />
  </div>
</template>

<script setup lang="ts">
import { inject, ref } from 'vue'

import type { AsyncDataRequestStatus } from '#app/composables/asyncData'
import type { CartItem } from '@backmarket/http-api/src/api-specs-checkout/cart/cart.types'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { useDebounceFn } from '@backmarket/utils/composables/useDebouncedFn'
import { RevButton } from '@ds/components/Button'

import type { ReplacementOption } from '~/api/checkout/cart/types'
import { getTrackingProduct } from '~/scopes/checkout/composables/useCheckoutTracking'
import useHandleUnauthorizedUser from '~/scopes/checkout/composables/useHandleUnauthorizedUser'
import { DEBOUNCE_DELAY } from '~/scopes/checkout/config/constants'

import translations from './Product.translations'
import Description from './components/Description/Description.vue'
import OutOfStock from './components/OutOfStock/OutOfStock.vue'
import PriceBlock from './components/PriceBlock/PriceBlock.vue'
import ProductIllustration from './components/ProductIllustration/ProductIllustration.vue'
import ArticleQuantitySelector from './components/QuantitySelector/ArticleQuantitySelector.vue'
import ShippingPromise from './components/ShippingPromise/ShippingPromise.vue'
import Stock from './components/Stock/Stock.vue'

const props = defineProps<{
  item: CartItem
  isOutOfStock: boolean
  replacements?: Array<ReplacementOption>
  replacementsStatus?: AsyncDataRequestStatus
}>()

const emit = defineEmits<{
  (e: 'updating'): void
  (e: 'updated'): void
}>()

const isCartLocked = inject('isCartLocked', false)

const i18n = useI18n()
const tracking = useTracking()
const $httpFetch = useHttpFetch()
const { handleUnauthorizedUser } = useHandleUnauthorizedUser()
const isRemoving = ref(false)

const {
  market: { defaultCurrency },
} = useMarketplace()

const updateItem = async (item: CartItem, action: 'delete' | 'update') => {
  try {
    emit('updating')

    await $httpFetch('/bm/cart/update_quantity', {
      method: 'POST',
      body: {
        action,
        listing_id: item.listingId,
        new_quantity: item.quantity,
        listing_price: item.price,
      },
    })

    if (action === 'delete') {
      const product = getTrackingProduct(item, defaultCurrency)
      if (product) {
        tracking.trackRemoveFromCart(product)
      }
    }

    emit('updated')
  } catch (error) {
    await handleUnauthorizedUser(
      error as Record<string, unknown>,
      '[CHECKOUT] Unhandled error updating quantity',
    )

    // Remove the loading status to let the user try again :)
    if (action === 'delete') {
      isRemoving.value = false
    }
  }
}

const handleDeleteButtonClick = async () => {
  isRemoving.value = true
  await updateItem(props.item, 'delete')
}

const handleQuantitySelectorChange = useDebounceFn(
  async function doUpdateQuantity(quantity: number) {
    if (quantity === 0) {
      await updateItem(props.item, 'delete')
    } else {
      await updateItem({ ...props.item, quantity }, 'update')
    }
  },
  DEBOUNCE_DELAY,
)
</script>

<style module>
.productGrid {
  grid-template-columns: auto 1fr;
  grid-template-areas:
    'illustration title'
    'delivery delivery'
    'price price'
    'manage manage';
}

.productGridOutOfStock {
  grid-template-areas:
    'illustration title title title'
    'price price manage manage';
}

@media only screen and (min-width: 768px) {
  .productGrid {
    grid-template-columns: auto 1fr auto;
    grid-template-areas:
      'illustration title price'
      'illustration delivery manage';
  }

  .productGridOutOfStock {
    grid-template-columns: auto 1fr auto;
    grid-template-areas:
      'illustration title price'
      'illustration title manage';
  }
}
</style>
